import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { apiJson } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, ArrowUpDown, Package } from 'lucide-react';
import { DataTable } from '@/components/tables/data-table';
import { buildSitelistByPackGroup, buildSitelistByFormatCounts, buildSitelistByLabelMembership } from '@shared/sitelist-builders';
import type { ResolvedRow, SitelistByPackGroupRow, SitelistByFormatSection } from '@shared/sitelist-builders';
import type { GroupsViewResponse, UnifiedGroup } from '@shared/unified-groups';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

interface CampaignRetailerResponse {
  retailerId?: number | null;
  retailer_id?: number | null;
}

export function SitelistTab({ campaignId }: { campaignId: number }) {
  const [viewMode, setViewMode] = useState<'pack-group' | 'format' | 'label'>('pack-group');
  const [isExporting, setIsExporting] = useState(false);

  // Use unified groups-view endpoint (Phase 0)
  const { data, isLoading, error } = useQuery<GroupsViewResponse>({
    queryKey: ['/api/campaigns', campaignId, 'groups-view'],
    queryFn: async () => {
      return apiJson(`/api/campaigns/${campaignId}/groups-view`);
    },
    enabled: !!campaignId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch campaign to get retailerId
  const { data: campaign } = useQuery<CampaignRetailerResponse>({
    queryKey: ['/api/campaigns', campaignId],
    queryFn: async () => {
      return apiJson(`/api/campaigns/${campaignId}`);
    },
    enabled: !!campaignId,
  });

  const retailerId = campaign?.retailerId ?? campaign?.retailer_id ?? null;

  // Fetch retailer labels
  const { data: retailerLabels = [] } = useQuery<Array<{ id: number; name: string; description?: string | null }>>({
    queryKey: ['/api/retailers', retailerId, 'labels'],
    queryFn: async () => {
      if (!retailerId) return [];
      const response = await fetch(`/api/retailers/${retailerId}/labels`);
      if (!response.ok) {
        // Labels are optional - don't fail if endpoint doesn't exist
        console.warn('[SitelistTab] Failed to fetch retailer labels:', response.status);
        return [];
      }
      return response.json();
    },
    enabled: !!retailerId,
  });

  // Build labelId -> labelName map
  const labelMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const label of retailerLabels) {
      map.set(label.id, label.name);
    }
    return map;
  }, [retailerLabels]);

  // Check if any group has a labelId set
  const hasAnyLabeledGroup = useMemo(() => {
    if (!data?.groups) return false;
    return data.groups.some(g => g.labelId != null && g.labelId > 0);
  }, [data?.groups]);

  // Build a map of groupKey -> UnifiedGroup for quick lookup
  const groupsMap = useMemo(() => {
    if (!data?.groups) return new Map<string, UnifiedGroup>();
    return new Map(data.groups.map(g => [g.groupName, g]));
  }, [data?.groups]);

  // Build views using shared builders - operate on resolvedRows from unified endpoint
  const packGroupView = useMemo(() => {
    if (!data?.resolvedRows) return [];
    return buildSitelistByPackGroup(data.resolvedRows, data.campaignType);
  }, [data]);

  const formatView = useMemo(() => {
    if (!data?.resolvedRows) return [];
    return buildSitelistByFormatCounts(data.resolvedRows, data.campaignType);
  }, [data]);

  const labelView = useMemo(() => {
    if (!data?.resolvedRows || !data?.groups) return [];
    return buildSitelistByLabelMembership(data.resolvedRows, data.groups, labelMap, data.campaignType);
  }, [data, labelMap]);

  // Get all pack/group names for column headers - derive from unified groups
  const allPackGroupNames = useMemo(() => {
    if (!data?.groups) return [];
    return data.groups.map(g => g.groupName).sort();
  }, [data]);

  // Get all format names for column headers
  const allFormatNames = useMemo(() => {
    if (!data?.resolvedRows) return [];
    const formatSet = new Set<string>();
    for (const row of data.resolvedRows) {
      if (row.formatName) formatSet.add(row.formatName);
    }
    return Array.from(formatSet).sort();
  }, [data]);

  // Get all label names for column headers (from labelView membership data)
  const allLabelNames = useMemo(() => {
    if (labelView.length === 0) return [];
    // Get label names from the first row's packGroupMembership keys
    const firstRow = labelView[0];
    return Object.keys(firstRow.packGroupMembership).sort();
  }, [labelView]);

  // Column definitions for Pack/Group view
  const packGroupColumns: ColumnDef<SitelistByPackGroupRow>[] = useMemo(() => {
    const baseColumns: ColumnDef<SitelistByPackGroupRow>[] = [
      {
        accessorKey: 'storeNumber',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Retailer Identifier
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => row.original.storeNumber || '-',
      },
      {
        accessorKey: 'storeName',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Store Name
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => row.original.storeName,
      },
    ];

    // Add dynamic pack/group columns with badges for pack-derived groups
    const packGroupColumns: ColumnDef<SitelistByPackGroupRow>[] = allPackGroupNames.map((pgName) => {
      const group = groupsMap.get(pgName);
      const isPackDerived = group?.seedPackId != null;
      
      return {
        id: `packGroup_${pgName}`,
        accessorFn: (row) => row.packGroupMembership[pgName],
        header: ({ column }) => {
          return (
            <div className="flex flex-col items-start">
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                className="h-8 px-2"
              >
                {pgName}
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
              {isPackDerived && (
                <span className="text-[10px] text-muted-foreground ml-2 flex items-center gap-1">
                  <Package className="h-3 w-3" />
                  Seeded from Pack
                </span>
              )}
            </div>
          );
        },
        cell: ({ row }) => {
          const value = row.original.packGroupMembership[pgName];
          return (
            <span className={value === 'YES' ? 'font-semibold text-green-600' : 'text-gray-400'}>
              {value}
            </span>
          );
        },
      };
    });

    return [...baseColumns, ...packGroupColumns];
  }, [allPackGroupNames]);

  // Use formatView directly - DataTable handles filtering
  const filteredFormatView = formatView;

  // Flatten format data for TanStack Table - one row per store with data for all groups
  interface FormatTableRow {
    storeId: number;
    storeNumber: string | null;
    storeName: string;
    [key: string]: any; // Dynamic format columns per group
  }

  const formatTableData: FormatTableRow[] = useMemo(() => {
    // Get all unique stores across all groups
    const allStores = new Map<number, {
      storeId: number;
      storeNumber: string | null;
      storeName: string;
      groupData: Map<string, Record<string, number>>;
    }>();

    for (const section of filteredFormatView) {
      for (const store of section.stores) {
        if (!allStores.has(store.storeId)) {
          allStores.set(store.storeId, {
            storeId: store.storeId,
            storeNumber: store.storeNumber ?? null,
            storeName: store.storeName,
            groupData: new Map(),
          });
        }
        allStores.get(store.storeId)!.groupData.set(section.packGroupName, store.formatCounts);
      }
    }

    // Build flat rows with dynamic columns for each group's formats
    return Array.from(allStores.values()).map((store) => {
      const row: FormatTableRow = {
        storeId: store.storeId,
        storeNumber: store.storeNumber,
        storeName: store.storeName,
      };

      // Add format columns for each group
      for (const section of filteredFormatView) {
        const counts = store.groupData.get(section.packGroupName);
        // Get formats for this specific group
        const groupFormats = new Set<string>();
        for (const s of section.stores) {
          Object.keys(s.formatCounts).forEach(f => groupFormats.add(f));
        }
        const sortedGroupFormats = Array.from(groupFormats).sort();
        
        for (const formatName of sortedGroupFormats) {
          const key = `${section.packGroupName}_${formatName}`;
          row[key] = counts?.[formatName] ?? null;
        }
      }

      return row;
    });
  }, [filteredFormatView]);

  // Column definitions for Format view with grouped headers
  const formatColumnsWithGroups: ColumnDef<FormatTableRow>[] = useMemo(() => {
    const baseColumns: ColumnDef<FormatTableRow>[] = [
      {
        accessorKey: 'storeNumber',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Retailer Identifier
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => row.original.storeNumber || '-',
      },
      {
        accessorKey: 'storeName',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Store Name
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => row.original.storeName,
      },
    ];

    // Add grouped columns for each pack/group with badges for pack-derived groups
    const groupColumns: ColumnDef<FormatTableRow>[] = filteredFormatView.map((section) => {
      // Get formats for this specific group
      const groupFormats = new Set<string>();
      for (const store of section.stores) {
        Object.keys(store.formatCounts).forEach(f => groupFormats.add(f));
      }
      const sortedGroupFormats = Array.from(groupFormats).sort();
      
      // Check if this group is pack-derived
      const group = groupsMap.get(section.packGroupName);
      const isPackDerived = group?.seedPackId != null;

      return {
        id: `group_${section.packGroupName}`,
        header: () => (
          <div className="flex flex-col items-start">
            <span className="font-semibold">{section.packGroupName}</span>
            {isPackDerived && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Package className="h-3 w-3" />
                Seeded from Pack
              </span>
            )}
          </div>
        ),
        columns: sortedGroupFormats.map((formatName) => ({
          id: `${section.packGroupName}_${formatName}`,
          accessorKey: `${section.packGroupName}_${formatName}`,
          header: ({ column }) => {
            return (
              <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                className="h-8 px-2"
              >
                {formatName}
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            );
          },
          cell: ({ row }) => {
            const value = row.original[`${section.packGroupName}_${formatName}`];
            return value !== null && value !== undefined ? value : '';
          },
        })),
      };
    });

    return [...baseColumns, ...groupColumns];
  }, [filteredFormatView]);

  // Label view table data - already in the right format (SitelistByPackGroupRow[])
  const labelTableData: SitelistByPackGroupRow[] = labelView;

  // Column definitions for Label view - YES/NO membership like pack-group view
  const labelColumnsWithGroups: ColumnDef<SitelistByPackGroupRow>[] = useMemo(() => {
    const baseColumns: ColumnDef<SitelistByPackGroupRow>[] = [
      {
        accessorKey: 'storeNumber',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Retailer Identifier
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => row.original.storeNumber || '-',
      },
      {
        accessorKey: 'storeName',
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              Store Name
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => row.original.storeName,
      },
    ];

    // Add dynamic label columns with YES/NO membership
    const labelColumns: ColumnDef<SitelistByPackGroupRow>[] = allLabelNames.map((labelName) => {
      return {
        id: `label_${labelName}`,
        accessorFn: (row) => row.packGroupMembership[labelName],
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              className="h-8 px-2"
            >
              {labelName}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          );
        },
        cell: ({ row }) => {
          const value = row.original.packGroupMembership[labelName];
          return (
            <span className={value === 'YES' ? 'font-semibold text-green-600' : 'text-gray-400'}>
              {value}
            </span>
          );
        },
      };
    });

    return [...baseColumns, ...labelColumns];
  }, [allLabelNames]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { fetchWithSupabaseTokenRaw } = await import('@/lib/http-supa');
      // Export supports 'pack-group', 'format', and 'label' view modes
      const buf = await fetchWithSupabaseTokenRaw(`/api/campaigns/${campaignId}/sitelist-export?viewMode=${viewMode}`);
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign-${campaignId}-sitelist.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export sitelist');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-2 text-muted-foreground">Loading sitelist data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <div className="mb-2">Error loading sitelist data</div>
        <div className="text-sm">{error instanceof Error ? error.message : 'Unknown error'}</div>
      </div>
    );
  }

  if (!data || data.resolvedRows.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <div className="mb-2">No sitelist data available</div>
        <div className="text-sm">This campaign does not have any stores or formats assigned.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Sitelist View</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={isExporting}
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'pack-group' | 'format' | 'label')}>
            <TabsList>
              <TabsTrigger value="pack-group">By Group</TabsTrigger>
              <TabsTrigger value="format">By Format</TabsTrigger>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <TabsTrigger 
                        value="label" 
                        disabled={!hasAnyLabeledGroup}
                        className={!hasAnyLabeledGroup ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        By Label
                      </TabsTrigger>
                    </div>
                  </TooltipTrigger>
                  {!hasAnyLabeledGroup && (
                    <TooltipContent>
                      <p>No labels assigned to groups</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Group Membership View */}
      {viewMode === 'pack-group' && (
        <Card>
          <CardHeader>
            <CardTitle>Store List by Group</CardTitle>
            <CardDescription>
              Shows which stores belong to each group. 
              {data.campaignType === 'pack' && ' Groups are derived from packs.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={packGroupColumns}
              data={packGroupView}
              searchKey="storeName"
              searchPlaceholder="Filter stores..."
              showColumnToggle={true}
              disablePagination={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Format Counts View - Single table with two header rows per group */}
      {viewMode === 'format' && (
        <Card>
          <CardHeader>
            <CardTitle>Store List by Format</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={formatColumnsWithGroups}
              data={formatTableData}
              searchKey="storeName"
              searchPlaceholder="Filter stores..."
              showColumnToggle={true}
              disablePagination={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Label View - Single table grouped by label */}
      {viewMode === 'label' && (
        <Card>
          <CardHeader>
            <CardTitle>Store List by Label</CardTitle>
            <CardDescription>
              Shows stores grouped by retailer labels assigned to groups.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={labelColumnsWithGroups}
              data={labelTableData}
              searchKey="storeName"
              searchPlaceholder="Filter stores..."
              showColumnToggle={true}
              disablePagination={true}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
